import { useState, useEffect, useCallback, useRef } from 'react';

const INACTIVITY_TIMEOUT_MS = 30 * 60 * 1000; // 30 Minuten
const WARNING_THRESHOLD_MS = 5 * 60 * 1000; // Warnung bei 5 Minuten verbleibend

interface UseInactivityTimerOptions {
  onTimeout: () => void;
  enabled?: boolean;
}

interface UseInactivityTimerResult {
  remainingTime: number; // in Millisekunden
  remainingTimeFormatted: string; // "MM:SS"
  isWarning: boolean; // true wenn < 5 Minuten verbleiben
  resetTimer: () => void;
}

export function useInactivityTimer({
  onTimeout,
  enabled = true,
}: UseInactivityTimerOptions): UseInactivityTimerResult {
  const [remainingTime, setRemainingTime] = useState(INACTIVITY_TIMEOUT_MS);
  const lastActivityRef = useRef(Date.now());
  const timeoutIdRef = useRef<NodeJS.Timeout | null>(null);

  const resetTimer = useCallback(() => {
    lastActivityRef.current = Date.now();
    setRemainingTime(INACTIVITY_TIMEOUT_MS);
  }, []);

  // Event-Handler für Aktivität
  useEffect(() => {
    if (!enabled) return;

    const handleActivity = () => {
      lastActivityRef.current = Date.now();
    };

    // Events die als "Aktivität" zählen
    const events = [
      'mousedown',
      'mousemove',
      'keydown',
      'scroll',
      'touchstart',
      'click',
    ];

    events.forEach((event) => {
      window.addEventListener(event, handleActivity, { passive: true });
    });

    return () => {
      events.forEach((event) => {
        window.removeEventListener(event, handleActivity);
      });
    };
  }, [enabled]);

  // Timer-Update alle Sekunde
  useEffect(() => {
    if (!enabled) return;

    const updateRemainingTime = () => {
      const elapsed = Date.now() - lastActivityRef.current;
      const remaining = Math.max(0, INACTIVITY_TIMEOUT_MS - elapsed);
      setRemainingTime(remaining);

      if (remaining === 0) {
        onTimeout();
      }
    };

    // Initial Update
    updateRemainingTime();

    // Interval für regelmäßige Updates
    const intervalId = setInterval(updateRemainingTime, 1000);

    return () => {
      clearInterval(intervalId);
      if (timeoutIdRef.current) {
        clearTimeout(timeoutIdRef.current);
      }
    };
  }, [enabled, onTimeout]);

  // Formatierung "MM:SS"
  const formatTime = (ms: number): string => {
    const totalSeconds = Math.floor(ms / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  return {
    remainingTime,
    remainingTimeFormatted: formatTime(remainingTime),
    isWarning: remainingTime <= WARNING_THRESHOLD_MS && remainingTime > 0,
    resetTimer,
  };
}
